import { useState } from "react";
import { IoMdMenu } from "react-icons/io";
import { IoIosClose } from "react-icons/io";
import { Link } from "react-scroll";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function Navbar({ isHomePage }) {
  const [toggle, setToggle] = useState(false);
  const navigate = useNavigate();

  // Hook for the translation
  const { t } = useTranslation();

  // Navigation Handler - wenn nicht auf Homepage, navigiere zur Homepage
  const handleNavClick = (sectionId) => {
    if (!isHomePage) {
      // Navigiere zur Homepage und scrolle zur Section
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  return (
    <>
      {/* 1. Äußerer schwebender Container, der die Navbar perfekt zentriert */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 w-[92%] max-w-[1200px] z-[9999] flex flex-col gap-2">
        
        {/* 2. Der eigentliche abgerundete Balken */}
        <div className="w-full h-[72px] bg-[#ffffff]/90 backdrop-blur-md border border-white/50 rounded-full shadow-lg flex items-center justify-between px-6 md:px-8">
          
          {/* Logo */}
          <NavLink to="/" className="flex items-center">
            <img
              src="./images/logo-black.svg"
              alt="site_logo"
              className="w-[140px] md:w-[160px] transition-transform duration-300 hover:scale-102"
            />
          </NavLink>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center">
            <ul className="flex items-center gap-[24px]">
              {isHomePage ? (
                <Link
                  to="home"
                  spy={true}
                  smooth={true}
                  offset={100}
                  duration={500}
                >
                  <li className="text-black font-semibold cursor-pointer hover:text-[#B588FF] transition-colors">
                    Home
                  </li>
                </Link>
              ) : (
                <li 
                  onClick={() => navigate('/')} 
                  className="text-black font-semibold cursor-pointer hover:text-[#B588FF] transition-colors"
                >
                  Home
                </li>
              )}

              {isHomePage ? (
                <Link
                  to="product"
                  spy={true}
                  smooth={true}
                  offset={0}
                  duration={700}
                >
                  <li className="text-[#A6A6A6] font-medium cursor-pointer hover:text-[#B588FF] transition-colors">
                    {t("nav1")}
                  </li>
                </Link>
              ) : (
                <li 
                  onClick={() => handleNavClick('product')} 
                  className="text-[#A6A6A6] font-medium cursor-pointer hover:text-[#B588FF] transition-colors"
                >
                  {t("nav1")}
                </li>
              )}

              {isHomePage ? (
                <Link
                  to="features"
                  spy={true}
                  smooth={true}
                  offset={-50}
                  duration={800}
                >
                  <li className="text-[#A6A6A6] font-medium cursor-pointer hover:text-[#B588FF] transition-colors">
                    {t("nav2")}
                  </li>
                </Link>
              ) : (
                <li 
                  onClick={() => handleNavClick('features')} 
                  className="text-[#A6A6A6] font-medium cursor-pointer hover:text-[#B588FF] transition-colors"
                >
                  {t("nav2")}
                </li>
              )}

              <NavLink to="/cv-anonymizer">
                <li className="text-[#A6A6A6] font-medium cursor-pointer hover:text-[#B588FF] transition-colors">
                  CV-Anonymisierer
                </li>
              </NavLink>
            </ul>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center md:gap-3">
            <NavLink to={"/login"}>
              <button className="px-6 py-2 rounded-full bg-white text-black border border-[#CECECE] font-medium transform transition-all duration-300 hover:scale-105 hover:shadow-md hover:border-[#B588FF] hover:text-[#B588FF] hover:bg-purple-50/50 active:scale-95 focus:outline-none">
                Login
              </button>
            </NavLink>

            <a href="/demo">
              <button className="px-6 py-2 rounded-full bg-gradient-to-r from-[#B588FF] to-[#8B5FFF] border border-[#B588FF] text-[#F8F8FA] font-medium transform transition-all duration-300 hover:scale-105 hover:shadow-lg hover:from-[#A576FF] hover:to-[#7A4EFF] active:scale-95 focus:outline-none">
                {t("trailBtn")}
              </button>
            </a>
          </div>

          {/* Mobile Hamburger Trigger */}
          <div
            className="md:hidden cursor-pointer text-2xl text-black hover:text-[#B588FF] transition-colors p-1"
            onClick={() => setToggle(!toggle)}
          >
            {toggle ? <IoIosClose fontSize={30} /> : <IoMdMenu fontSize={26} />}
          </div>
        </div>

        {/* 3. Elegantes schwebendes Mobile-Menü (nur sichtbar, wenn getoggled) */}
        <div
          className={
            toggle
              ? "w-full p-6 bg-white/95 backdrop-blur-md rounded-[24px] shadow-xl border border-purple-100 md:hidden transition-all duration-300 ease-in-out"
              : "hidden"
          }
        >
          <ul className="flex flex-col gap-1">
            <li 
              className="p-3 text-black font-semibold cursor-pointer rounded-xl hover:bg-purple-50 transition-colors" 
              onClick={() => {
                setToggle(false);
                if (isHomePage) {
                  document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  navigate('/');
                }
              }}
            >
              Home
            </li>
            <li 
              className="p-3 text-[#A6A6A6] font-medium cursor-pointer rounded-xl hover:bg-purple-50 hover:text-black transition-all" 
              onClick={() => {
                setToggle(false);
                handleNavClick('product');
              }}
            >
              {t("nav1")}
            </li>
            <li 
              className="p-3 text-[#A6A6A6] font-medium cursor-pointer rounded-xl hover:bg-purple-50 hover:text-black transition-all" 
              onClick={() => {
                setToggle(false);
                handleNavClick('features');
              }}
            >
              {t("nav2")}
            </li>

            <NavLink to="/cv-anonymizer" onClick={() => setToggle(false)}>
              <li className="p-3 text-[#A6A6A6] font-medium cursor-pointer rounded-xl hover:bg-purple-50 hover:text-black transition-all">
                CV-Anonymisierer
              </li>
            </NavLink>

            <div className="w-full flex flex-col mt-4 gap-3">
              <NavLink to={"/login"} onClick={() => setToggle(false)}>
                <button className="w-full py-2.5 rounded-full bg-white text-black border border-[#CECECE] font-medium transition-all hover:bg-purple-50">
                  Login
                </button>
              </NavLink>

              <a href="/demo" onClick={() => setToggle(false)}>
                <button className="w-full py-2.5 rounded-full bg-gradient-to-r from-[#B588FF] to-[#8B5FFF] text-white font-medium shadow-md transition-all">
                  {t("trailBtn")}
                </button>
              </a>
            </div>
          </ul>
        </div>
      </div>

      {/* Spacer entfernt - Hero-Hintergrund soll bis ganz nach oben reichen */}
    </>
  );
}

export default Navbar;